import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { createUseStyles, Select } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { getEntityValue } from 'lib/services/valueLists';
import {
    applyLineSmartFieldOverridesToInputParams,
    getLineSmartFieldOverrides,
    toCellValidationError,
} from './applyLineSmartFieldOverrides';

import styles from './styles';

const useStyles = createUseStyles(styles, 'SelectCell');

const hasSelectObjectWithId = (raw) =>
    raw != null &&
    typeof raw === 'object' &&
    Object.prototype.hasOwnProperty.call(raw, 'value') &&
    raw.value !== undefined &&
    raw.value !== null;

const toSelectOption = (raw) => {
    if (hasSelectObjectWithId(raw)) {
        const label = raw.description ?? raw.label ?? '';
        return { label, value: String(raw.value) };
    }
    if (raw === null || raw === undefined || raw === '') return undefined;
    return { label: String(raw), value: String(raw) };
};

const SelectCell = memo((params) => {
    const { t } = useWeb5Context();
    const [value, setValue] = useState(() => toSelectOption(params.value));
    const [error, setError] = useState('');
    const resolvedPrimitiveRef = useRef(null);
    const classes = useStyles();

    const field = params.colDef.field;
    const lineOverrides = getLineSmartFieldOverrides(params.data, field);
    const { onChange, isMulti, list, ...inputParams } = applyLineSmartFieldOverridesToInputParams(
        params.inputParams,
        lineOverrides,
    );
    const isLineReadOnly = !!(lineOverrides?.readOnly || lineOverrides?.hidden);

    const handleOnChange = useCallback(
        (newValue) => {
            const id = params.data.id || params.data.Id || params.data.ID || '';
            onChange({ rowIndex: params.node.rowIndex, id, field, value: newValue });
            setValue(newValue);
        },
        [onChange, field, params],
    );

    useEffect(() => {
        const rowIndex = params.node.rowIndex;
        const rowError = inputParams?.errors?.rows?.[rowIndex]?.error;
        setError(toCellValidationError(rowError));
    }, [inputParams.errors, params]);

    useEffect(() => {
        const raw = params?.value;
        if (hasSelectObjectWithId(raw)) {
            const option = toSelectOption(raw);
            resolvedPrimitiveRef.current = option.value;
            setValue((prev) =>
                prev?.value === option.value && prev?.label === option.label ? prev : option,
            );
            return;
        }
        if (raw === null || raw === undefined || raw === '') {
            resolvedPrimitiveRef.current = null;
            setValue(undefined);
            return;
        }

        const rawKey = String(raw);
        if (resolvedPrimitiveRef.current === rawKey) {
            return;
        }

        getEntityValue(list, isMulti ? raw : [raw]).then((result) => {
            if (!result?.data) return;
            const finalValue = isMulti
                ? params?.value?.map((v) => {
                      const row = result.data[v] ?? result.data[String(v)];
                      return {
                          label: row?.description,
                          value: String(row?.id),
                      };
                  })
                : (() => {
                      const row = result.data[raw] ?? result.data[String(raw)];
                      if (row == null) return null;
                      return {
                          label: row.description,
                          value: String(row.id),
                      };
                  })();
            if (finalValue != null) {
                resolvedPrimitiveRef.current = rawKey;
                setValue(finalValue);
            }
        });
    }, [isMulti, list, params?.value]);

    if (!params.inputParams) return null;

    const placeholder = isLineReadOnly
        ? t('label_advanced_filter_option_empty')
        : t('label_selectone');

    return (
        <div className={['w5-cell', classes.editableCell].join(' ')}>
            <Select
                value={value}
                onChange={handleOnChange}
                list={list}
                isMulti={isMulti}
                {...inputParams}
                isReadOnly={isLineReadOnly}
                readOnly={isLineReadOnly}
                placeholder={placeholder}
                error={error}
                isFullWidth
            />
        </div>
    );
});

export default SelectCell;
